/**
 * Same fetch-wrapper as mobile, ported to the browser. Token stored in
 * localStorage (web doesn't have secure-store; production should swap
 * to httpOnly cookie via a backend session endpoint in Phase 2).
 */

const KEY_SESSION = "isale.session";

export interface Session {
  accessToken: string;
  refreshToken: string;
  userId: string;
  workspaceId: string;
}

interface JwtPayload {
  sub?: string;
  exp?: number;
  email?: string;
  [k: string]: unknown;
}

/**
 * Decode a JWT payload without verifying the signature. Returns null if the
 * token is malformed. Used only for client-side staleness checks — the server
 * still re-verifies on every request.
 */
function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = b64.length % 4 ? "=".repeat(4 - (b64.length % 4)) : "";
    const json = atob(b64 + pad);
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * Returns true when the stored token should NOT be trusted:
 *   - `exp` is in the past (naturally expired)
 *   - `email` ends with `@isale.local` (legacy DEV_MOCK token — Worker no
 *     longer honors these after 2026-05-20 when DEV_MOCK_AUTH_ENABLED flipped
 *     to false on the dev env). We treat them as expired so the user is
 *     redirected to sign-in instead of seeing constant 401s on every query.
 */
function isStaleToken(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload) return true;
  if (typeof payload.exp === "number" && payload.exp * 1000 < Date.now()) return true;
  if (typeof payload.email === "string" && payload.email.endsWith("@isale.local")) return true;
  return false;
}

export const sessionStore = {
  load(): Session | null {
    try {
      const raw = localStorage.getItem(KEY_SESSION);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Session;
      if (!parsed.accessToken || isStaleToken(parsed.accessToken)) {
        // Purge stale / DEV_MOCK leftovers proactively so the app boots into
        // the sign-in screen instead of flashing the dashboard with broken
        // queries before the first 401 trips a refresh.
        localStorage.removeItem(KEY_SESSION);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  },
  save(s: Session) {
    localStorage.setItem(KEY_SESSION, JSON.stringify(s));
  },
  clear() {
    localStorage.removeItem(KEY_SESSION);
  },
};

export interface ProblemDetail {
  type: string;
  title: string;
  status: number;
  code: string;
  detail: string;
  [extra: string]: unknown;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ProblemDetail | string,
  ) {
    super(typeof body === "string" ? body : body.detail);
    this.name = "ApiError";
  }
}

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:8787";

interface RequestOptions extends Omit<RequestInit, "body"> {
  json?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  skipAuth?: boolean;
}

let refreshInFlight: Promise<Session | null> | null = null;

async function tryRefresh(): Promise<Session | null> {
  const existing = sessionStore.load();
  if (!existing) return null;
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    // Retry once on transient failures. Cloudflare Workers cold-start
    // or brief network blips can flake the first POST; clearing the
    // session on a single failure means a 30-second deploy/restart
    // window can kick all active users back to /sign-in. The refresh
    // endpoint rotates atomically (old token revoked + new issued in
    // one txn — services/isale-service/src/routes/auth/refresh.ts L92),
    // so a retry against the SAME refresh_token is safe: either the
    // first attempt landed (we'd see 401 because the token was just
    // revoked — handled below) or it didn't (the rotation never ran).
    let lastError: unknown = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await fetch(`${BASE_URL}/v1/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: existing.refreshToken }),
        });
        if (res.ok) {
          const body = (await res.json()) as {
            access_token: string;
            refresh_token: string;
          };
          const next: Session = {
            ...existing,
            accessToken: body.access_token,
            refreshToken: body.refresh_token,
          };
          sessionStore.save(next);
          return next;
        }
        // 401 on a refresh = the token is genuinely invalid/revoked.
        // 5xx or network error = give the backend one more chance.
        if (res.status === 401) {
          lastError = `refresh returned 401`;
          break;
        }
        lastError = `refresh returned ${res.status}`;
      } catch (err) {
        lastError = err;
      }
      // Brief backoff before retry (only on transient failure path).
      if (attempt === 0) {
        await new Promise((r) => setTimeout(r, 400));
      }
    }
    // Both attempts failed — clear session + fire UX event.
    console.warn("[auth] refresh failed after retry:", lastError);
    sessionStore.clear();
    // Only fire the UX event here — the AuthExpiredModal owns the
    // user-visible redirect flow. We intentionally do NOT fire
    // `isale:session-cleared` from this path: that event syncs the
    // Zustand session to null, which causes Layout (L149) to
    // `<Navigate to="/sign-in" />` IMMEDIATELY, racing the modal
    // open and yanking the user away before they see the 5-second
    // countdown warning. Instead, AuthExpiredModal fires
    // `isale:session-cleared` itself right before `navigate(...)`,
    // keeping Layout's stale-session view alive for the duration of
    // the countdown.
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("isale:auth-expired"));
    }
    return null;
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(path.startsWith("http") ? path : `${BASE_URL}${path}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

export async function apiRequest<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const headers = new Headers(opts.headers ?? {});
  if (opts.json !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!opts.skipAuth) {
    const session = sessionStore.load();
    if (session) headers.set("Authorization", `Bearer ${session.accessToken}`);
  }

  const { json: _json, query: _query, skipAuth: _skip, ...passthrough } = opts;
  const init: RequestInit = {
    ...passthrough,
    headers,
    body: opts.json !== undefined ? JSON.stringify(opts.json) : undefined,
  };
  void _json;
  void _query;
  void _skip;

  let res = await fetch(buildUrl(path, opts.query), init);

  if (res.status === 401 && !opts.skipAuth) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      headers.set("Authorization", `Bearer ${refreshed.accessToken}`);
      res = await fetch(buildUrl(path, opts.query), { ...init, headers });
    }
  }

  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") ?? "";
  const isJson = ct.includes("application/json");
  const body = isJson ? await res.json() : await res.text();
  if (!res.ok) {
    // Cross-cutting: 403 CONSENT_REQUIRED → fire isale:consent-required so
    // a globally-mounted AiConsentRepromptModal can prompt the user to
    // re-acknowledge AI processing terms before the offending call can
    // succeed. Backend attaches `current_version` to ProblemDetail via
    // AppError extras (services/isale-service/src/lib/consent.ts L82-110).
    // Web parity port of mobile client.ts L155-167. Closes audit §6 B1.
    if (res.status === 403 && isJson) {
      const pd = body as ProblemDetail;
      if (pd.code === "CONSENT_REQUIRED" && typeof window !== "undefined") {
        const version =
          typeof pd.current_version === "string" ? pd.current_version : "current";
        window.dispatchEvent(
          new CustomEvent("isale:consent-required", { detail: { version } }),
        );
      }
    }
    // Cross-cutting: 402 QUOTA_COST_CAP_REACHED (F-Q1) → fire a global event so
    // the CostCapModal (mounted in App.tsx) shows a friendly localized upgrade
    // prompt. Web parity of mobile client.ts. Callers still see the thrown
    // ApiError so they can short-circuit local state.
    if (res.status === 402 && isJson) {
      const pd = body as ProblemDetail;
      if (pd.code === "QUOTA_COST_CAP_REACHED" && typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("isale:cost-cap-reached"));
      }
    }
    // CF-M3: 429 QUOTA_EXCEEDED (monthly video / AI-mode cap — the most common
    // render block) → fire a distinct global event so the upgrade modal shows a
    // localized "out of videos this month → upgrade" prompt with a Billing CTA,
    // separate from the 402 cost-cap path. Callers still see the thrown error.
    if (res.status === 429 && isJson) {
      const pd = body as ProblemDetail;
      if (pd.code === "QUOTA_EXCEEDED" && typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("isale:quota-exceeded"));
      }
    }
    throw new ApiError(res.status, body as ProblemDetail | string);
  }
  return body as T;
}

export const api = {
  get: <T,>(path: string, query?: RequestOptions["query"]) =>
    apiRequest<T>(path, { method: "GET", query }),
  post: <T,>(path: string, json?: unknown, opts?: RequestOptions) =>
    apiRequest<T>(path, { ...(opts ?? {}), method: "POST", json }),
  patch: <T,>(path: string, json?: unknown) =>
    apiRequest<T>(path, { method: "PATCH", json }),
  delete: <T,>(path: string) => apiRequest<T>(path, { method: "DELETE" }),
};
